import { Dispatch, SetStateAction, useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import clock from '../../../../../../assets/images/icons/clock.svg';
import coins from '../../../../../../assets/images/icons/coins.svg';
import useApiCall from '../../../../../../hooks/useApiCall';
import useGetGeneralAppData from '../../../../../../hooks/useGetGeneralAppData';
import ConsultsService from '../../../../../../services/ConsultsService';
import floatToCurrency from '../../../../../../utils/floatToCurrency';
import { IGetRouteOptionApiResponse, TrajetoSelecaoIda, TrajetoSelecaoVolum, TrajetosIda, TrajetosVolum } from '../../../../types';
import ChangePredefinedOptionModal from './components/ChangePredefinedOptionModal';
import { Container, PredefinedOption } from './styles';

interface IPredefinedOptions {
  resultHeaderWidth: number;
  predefinedOptions: TrajetosIda[];
  selectedGoingRoute: TrajetosIda;
  selectedReturningRoute: TrajetosVolum;
  changePredefinedOption: (option: TrajetosIda) => Promise<void>;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  canEnableActions: boolean;
}

export default function PredefinedOptions({ resultHeaderWidth, predefinedOptions, selectedGoingRoute, selectedReturningRoute, changePredefinedOption, setIsLoading, canEnableActions }: IPredefinedOptions) {
  const isSomePredefinedSelected = selectedGoingRoute?.trajeto === selectedReturningRoute?.trajeto;
  const predefinedOptionSelected = isSomePredefinedSelected ? selectedGoingRoute?.trajeto : 4;

  const [changePredefinedOptionModalShow, setChangePredefinedOptionModalShow] = useState(false);
  const [targetOption, setTargetOption] = useState({} as TrajetosIda);
  const [correspondingGoingRoute, setCorrespondingGoingRoute] = useState<TrajetoSelecaoIda[]>([]);
  const [correspondingReturningRoute, setCorrespondingReturningRoute] = useState<TrajetoSelecaoVolum[]>([]);

  const { apiCall } = useApiCall();
  const { userCode, userProfile, companyCode } = useGetGeneralAppData();
  const { consultCode } = useParams();

  const handleOpenChangePredefinedOptionModal = useCallback(async (option: TrajetosIda) => {
    setTargetOption(option);

    await Promise.all([
      await apiCall({
        apiToCall: ConsultsService.getRouteOfOption,
        queryParams: {
          codUsu: userCode,
          perfilUsu: userProfile,
          codEmpresa: companyCode,
          consultCode,
          sentido: 1,
          trajeto: option.trajeto,
          rota: option.rota,
        },
        onStartLoad: () => setIsLoading(true),
        onEndLoad: () => setIsLoading(false),
        actionAfterResponse: async (response: IGetRouteOptionApiResponse) => {
          if (!response.codigo) {
            return;
          }
          setCorrespondingGoingRoute(response.trajetoSelecao);
        }
      }),
      await apiCall({
        apiToCall: ConsultsService.getRouteOfOption,
        queryParams: {
          codUsu: userCode,
          perfilUsu: userProfile,
          codEmpresa: companyCode,
          consultCode,
          sentido: 2,
          trajeto: option.trajeto,
          rota: option.rota,
        },
        onStartLoad: () => setIsLoading(true),
        onEndLoad: () => setIsLoading(false),
        actionAfterResponse: async (response: IGetRouteOptionApiResponse) => {
          if (!response.codigo) {
            return;
          }
          setCorrespondingReturningRoute(response.trajetoSelecao);
        }
      }),
    ]);

    setChangePredefinedOptionModalShow(true);
  }, [apiCall, companyCode, consultCode, setIsLoading, userCode, userProfile]);

  return (
    <Container resultHeaderWidth={resultHeaderWidth}>
      {predefinedOptions.map((opt, index) => (
        <PredefinedOption
          key={opt.trajeto}
          backgroundIndex={index + 1}
          selected={predefinedOptionSelected === opt.trajeto}
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          onClick={predefinedOptionSelected === opt.trajeto ? () => {} : () => handleOpenChangePredefinedOptionModal(opt)}
        >
          <h6>Opção {opt.trajeto}</h6>
          <div>
            <div>
              <img src={clock} alt="" className='sameAsTheme' />
              <span>{opt.tempoTotTj} min</span>
            </div>
            <div>
              <img src={coins} alt="" className='sameAsTheme' />
              <span>{floatToCurrency(opt.valorVtTotal)}</span>
            </div>
          </div>
        </PredefinedOption>
      ))}

      {!isSomePredefinedSelected && (
        <PredefinedOption key={predefinedOptionSelected} backgroundIndex={4} selected={true}>
          <h6>Personalizada</h6>
          <div>
            <div>
              <img src={clock} alt="" className='sameAsTheme' />
              <span>{selectedGoingRoute?.tempoTotTj + selectedReturningRoute?.tempoTotTj} min</span>
            </div>
            <div>
              <img src={coins} alt="" className='sameAsTheme' />
              <span>{floatToCurrency(selectedGoingRoute?.valorVtTotal + selectedReturningRoute?.valorVtTotal)}</span>
            </div>
          </div>
        </PredefinedOption>
      )}

      <ChangePredefinedOptionModal
        changePredefinedOptionModalShow={changePredefinedOptionModalShow}
        setChangePredefinedOptionModalShow={setChangePredefinedOptionModalShow}
        currentOption={predefinedOptionSelected}
        targetOption={targetOption}
        changePredefinedOption={changePredefinedOption}
        correspondingGoingRoute={correspondingGoingRoute}
        correspondingReturningRoute={correspondingReturningRoute}
        canEnableActions={canEnableActions}
      />
    </Container>
  );
}
